import { useState, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@cieda/djayma-ui";
import { Plus, Upload, AlertCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { ApiError } from "@/lib/api-error-handler";
import AdminLayout from "@/components/admin/AdminLayout";
import { useAdminPageTitle } from "@/hooks/admin/useAdminPageTitle";
import { SearchInput } from "@/components/admin/shared/SearchInput";
import { ProductTable } from "@/components/admin/products/ProductTable";
import {
  ProductFilters,
  type ProductFilterValues,
} from "@/components/admin/products/ProductFilters";
import { ProductForm } from "@/components/admin/products/ProductForm";
import { CSVImportModal } from "@/components/admin/products/CSVImportModal";
import { ProductsErrorBoundary } from "@/components/admin/products/ProductsErrorBoundary";
import { StockDrawer } from "@/components/admin/products/StockDrawer";
import { BulkActionBar } from "@/components/admin/products/BulkActionBar";
import { BulkActivateModal } from "@/components/admin/products/BulkActivateModal";
import { BulkDeactivateModal } from "@/components/admin/products/BulkDeactivateModal";
import { BulkDeleteModal } from "@/components/admin/products/BulkDeleteModal";
import { BulkPriceModal } from "@/components/admin/products/BulkPriceModal";
import { BulkCategoryModal } from "@/components/admin/products/BulkCategoryModal";
import { BulkThresholdModal } from "@/components/admin/products/BulkThresholdModal";
import { BulkExportModal } from "@/components/admin/products/BulkExportModal";
import { ProductCommandPalette } from "@/components/admin/products/ProductCommandPalette";
import { KeyboardShortcutsCheatsheet } from "@/components/admin/products/KeyboardShortcutsCheatsheet";
import {
  useProductsList,
  useCreateProduct,
  useUpdateProduct,
  useDeactivateProduct,
  useImportProducts,
} from "@/hooks/admin/useProducts";
import { useProductSelection } from "@/hooks/admin/useProductSelection";
import { useBulkActions } from "@/hooks/admin/useBulkActions";
import { useKeyboardShortcuts } from "@/hooks/admin/useKeyboardShortcuts";
import { productsApi } from "@/lib/admin-api/products";
import type { Product, CategorySlug } from "@/lib/validation/product";
import type { SortState } from "@/components/admin/shared";

// ---------------------------------------------------------------------------
// Page state
// ---------------------------------------------------------------------------

const DEFAULT_FILTERS: ProductFilterValues = {
  brand: null,
  category: null,
  status: null,
  stock: null,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function Products() {
  return (
    <ProductsErrorBoundary>
      <ProductsPageInner />
    </ProductsErrorBoundary>
  );
}

function ProductsPageInner() {
  const { t } = useTranslation("admin");
  const navigate = useNavigate();
  useAdminPageTitle("Produits");

  // Filters / search / pagination state
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<ProductFilterValues>(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState | null>(null);

  // Modal state
  const [formOpen, setFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [viewProduct, setViewProduct] = useState<Product | null>(null);

  // Slice 1B state
  const [stockDrawerOpen, setStockDrawerOpen] = useState(false);
  const [stockDrawerProduct, setStockDrawerProduct] = useState<Product | null>(
    null,
  );
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [thresholdModalOpen, setThresholdModalOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [cmdPaletteOpen, setCmdPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [focusedRowIndex, setFocusedRowIndex] = useState(0);

  // Refs
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Data fetching
  const { data, isLoading, isError, refetch } = useProductsList({
    search: search || undefined,
    brand: filters.brand ?? undefined,
    category: filters.category ?? undefined,
    status: filters.status ?? undefined,
    stock: filters.stock ?? undefined,
    page,
    limit: 20,
    sort_by: sort?.column,
    sort_dir: sort?.direction,
  });

  const products = useMemo(() => data?.products ?? [], [data?.products]);
  const totalFiltered = data?.pagination?.total ?? products.length;

  // Hooks for selection + bulk
  const selection = useProductSelection(products);
  const bulkActions = useBulkActions();

  const selectedProducts = useMemo(
    () => products.filter((p) => selection.selectedIds.has(p.id)),
    [products, selection.selectedIds],
  );

  // Mutations
  const createMutation = useCreateProduct();
  const updateMutation = useUpdateProduct();
  const deactivateMutation = useDeactivateProduct();
  const importMutation = useImportProducts();

  // Handlers ----------------------------------------------------------------

  // Format an error for toast display. When the backend returns field-level
  // details (ApiError.details), surface them so admins know exactly which
  // field failed validation instead of "Erreur lors de la création". Fixes
  // audit finding S-H17.
  const formatApiError = (err: unknown, fallback: string): string => {
    if (err instanceof ApiError) return err.toHumanMessage();
    if (err instanceof Error) return err.message;
    return fallback;
  };

  const handleCreateSubmit = useCallback(
    async (payload: Record<string, unknown>) => {
      try {
        await createMutation.mutateAsync(payload);
        toast.success("Produit créé avec succès");
      } catch (err) {
        toast.error(formatApiError(err, "Erreur lors de la création"));
        throw err;
      }
    },
    [createMutation],
  );

  const handleUpdateSubmit = useCallback(
    async (payload: Record<string, unknown>) => {
      if (!editingProduct) return;
      try {
        await updateMutation.mutateAsync({
          id: editingProduct.id,
          data: payload,
        });
        toast.success("Produit mis à jour");
      } catch (err) {
        toast.error(formatApiError(err, "Erreur lors de la mise à jour"));
        throw err;
      }
    },
    [editingProduct, updateMutation],
  );

  const handleImportConfirm = useCallback(
    async (rows: readonly Record<string, unknown>[]) => {
      try {
        const result = await importMutation.mutateAsync(rows);
        toast.success(`${result.imported} produit(s) importé(s)`);
        if (result.errors.length > 0) {
          toast.warning(`${result.errors.length} ligne(s) en erreur`);
        }
        setImportOpen(false);
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Erreur lors de l'importation",
        );
      }
    },
    [importMutation],
  );

  const handleEdit = useCallback((product: Product) => {
    setEditingProduct(product);
    setFormOpen(true);
  }, []);

  const handleView = useCallback((product: Product) => {
    setViewProduct(product);
  }, []);

  // Phase-2 bug #12: the backend exposes DELETE /api/admin/products/[id]
  // but there was no single-row UI button — only bulk deactivate via the
  // selection checkbox. Add a per-row trash icon that soft-deactivates
  // after a confirm() dialog. A toast with undo would be nicer UX but
  // needs a reactivate endpoint the UI doesn't currently expose.
  const handleDelete = useCallback(
    (product: Product) => {
      const confirmed = window.confirm(
        `Désactiver le produit « ${product.name} » ?\n\n` +
          `Il sera masqué du catalogue mais reste restaurable depuis la liste des produits inactifs.`,
      );
      if (!confirmed) return;
      deactivateMutation.mutate(product.id, {
        onSuccess: () => {
          toast.success(`« ${product.name} » désactivé`);
        },
        onError: (err) => {
          toast.error(
            err instanceof ApiError
              ? err.message
              : `Échec de la désactivation : ${err instanceof Error ? err.message : "erreur inconnue"}`,
          );
        },
      });
    },
    [deactivateMutation],
  );

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingProduct(null);
  }, []);

  const handleNewProduct = useCallback(() => {
    setEditingProduct(null);
    setFormOpen(true);
  }, []);

  const handleSort = useCallback((nextSort: SortState) => {
    setSort(nextSort);
  }, []);

  const handlePageChange = useCallback((nextPage: number) => {
    setPage(nextPage);
  }, []);

  const handleFiltersChange = useCallback((next: ProductFilterValues) => {
    setFilters(next);
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleOpenStockDrawer = useCallback((product: Product) => {
    setStockDrawerProduct(product);
    setStockDrawerOpen(true);
  }, []);

  // CSV export via Blob download
  const handleExportCsv = useCallback(async () => {
    try {
      const blob = await productsApi.exportCsv({
        search: search || undefined,
        brand: filters.brand ?? undefined,
        category: filters.category ?? undefined,
        status: filters.status ?? undefined,
        stock: filters.stock ?? undefined,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      link.download = `produits-${stamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
      setExportModalOpen(false);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Erreur lors de l'export",
      );
    }
  }, [search, filters]);

  // Keyboard shortcuts
  useKeyboardShortcuts({
    onNavigateUp: useCallback(() => {
      setFocusedRowIndex((i) => Math.max(0, i - 1));
    }, []),
    onNavigateDown: useCallback(() => {
      setFocusedRowIndex((i) => Math.min(products.length - 1, i + 1));
    }, [products.length]),
    onToggleSelect: useCallback(() => {
      const p = products[focusedRowIndex];
      if (p) selection.toggle(p.id);
    }, [products, focusedRowIndex, selection]),
    onSelectAll: useCallback(() => {
      selection.toggleAll(products);
    }, [selection, products]),
    onClear: useCallback(() => {
      selection.clear();
    }, [selection]),
    onEdit: useCallback(() => {
      const p = products[focusedRowIndex];
      if (p) handleEdit(p);
    }, [products, focusedRowIndex, handleEdit]),
    onStockDrawer: useCallback(() => {
      const p = products[focusedRowIndex];
      if (p) handleOpenStockDrawer(p);
    }, [products, focusedRowIndex, handleOpenStockDrawer]),
    onCommandPalette: useCallback(() => {
      setCmdPaletteOpen((v) => !v);
    }, []),
    onFocusSearch: useCallback(() => {
      searchInputRef.current?.focus();
    }, []),
    onShowShortcuts: useCallback(() => {
      setShortcutsOpen(true);
    }, []),
  });

  // Bulk action handlers ----------------------------------------------------
  const bulkIds = useMemo(
    () => Array.from(selection.selectedIds),
    [selection.selectedIds],
  );

  const handleBulkActivate = useCallback(async () => {
    await bulkActions.activate.mutateAsync(bulkIds);
    setActivateModalOpen(false);
    selection.clear();
  }, [bulkActions.activate, bulkIds, selection]);

  const handleBulkDeactivate = useCallback(async () => {
    await bulkActions.deactivate.mutateAsync(bulkIds);
    setDeactivateModalOpen(false);
    selection.clear();
  }, [bulkActions.deactivate, bulkIds, selection]);

  const handleBulkDelete = useCallback(async () => {
    await bulkActions.bulkDelete.mutateAsync(bulkIds);
    setDeleteModalOpen(false);
    selection.clear();
  }, [bulkActions.bulkDelete, bulkIds, selection]);

  const handleBulkPrice = useCallback(
    async (mode: "absolute" | "percent", value: number) => {
      await bulkActions.price.mutateAsync({ ids: bulkIds, mode, value });
      setPriceModalOpen(false);
      selection.clear();
    },
    [bulkActions.price, bulkIds, selection],
  );

  const handleBulkCategory = useCallback(
    async (category: CategorySlug) => {
      await bulkActions.category.mutateAsync({ ids: bulkIds, category });
      setCategoryModalOpen(false);
      selection.clear();
    },
    [bulkActions.category, bulkIds, selection],
  );

  const handleBulkThreshold = useCallback(
    async (threshold: number | null) => {
      await bulkActions.threshold.mutateAsync({ ids: bulkIds, threshold });
      setThresholdModalOpen(false);
      selection.clear();
    },
    [bulkActions.threshold, bulkIds, selection],
  );

  // Render ------------------------------------------------------------------

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="font-display text-2xl font-semibold text-stone-900">
              {t("nav.products")}
            </h1>
            <p className="mt-1 text-sm text-stone-500">
              Gérez votre catalogue de produits
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setImportOpen(true)}
              className="gap-1.5"
            >
              <Upload className="h-4 w-4" />
              <span className="hidden sm:inline">Importer CSV</span>
            </Button>
            <Button size="sm" onClick={handleNewProduct} className="gap-1.5">
              <Plus className="h-4 w-4" />
              Nouveau produit
            </Button>
          </div>
        </div>

        {/* Search + Filters */}
        <div className="space-y-3">
          <div className="max-w-sm">
            <SearchInput
              ref={searchInputRef}
              value={search}
              onChange={handleSearchChange}
              placeholder="Rechercher un produit..."
            />
          </div>
          <ProductFilters values={filters} onChange={handleFiltersChange} />
        </div>

        {/* Error state with retry */}
        {isError && (
          <div
            className="flex flex-col gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
            role="alert"
          >
            <div className="flex items-center gap-2 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
              Impossible de charger les produits. Veuillez réessayer.
            </div>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 gap-1.5 border-red-300 text-red-700 hover:bg-red-100"
              onClick={() => void refetch()}
            >
              <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
              Réessayer
            </Button>
          </div>
        )}

        {/* Product table */}
        <ProductTable
          products={products}
          pagination={data?.pagination}
          onPageChange={handlePageChange}
          onSort={handleSort}
          currentSort={sort}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onStockDrawer={handleOpenStockDrawer}
          isLoading={isLoading}
          selection={selection}
          focusedRowIndex={focusedRowIndex}
          onFocusedRowChange={setFocusedRowIndex}
        />

        {/* Product form modal (create / edit) */}
        <ProductForm
          open={formOpen}
          onClose={handleFormClose}
          product={editingProduct}
          onSubmit={editingProduct ? handleUpdateSubmit : handleCreateSubmit}
        />

        {/* CSV import modal */}
        <CSVImportModal
          open={importOpen}
          onClose={() => setImportOpen(false)}
          onConfirm={handleImportConfirm}
          isImporting={importMutation.isPending}
        />

        {/* View product detail modal */}
        {viewProduct && (
          <ProductDetailModal
            product={viewProduct}
            onClose={() => setViewProduct(null)}
          />
        )}

        {/* Stock drawer */}
        <StockDrawer
          product={stockDrawerProduct}
          open={stockDrawerOpen}
          onOpenChange={(v) => {
            setStockDrawerOpen(v);
            if (!v) setStockDrawerProduct(null);
          }}
        />

        {/* Bulk action bar */}
        <BulkActionBar
          selectedIds={selection.selectedIds}
          selectedProducts={selectedProducts}
          totalFiltered={totalFiltered}
          isAllFilteredSelected={selection.isAllFilteredSelected}
          onSelectAllFiltered={() => {
            // For now select all loaded products; server-side "all filtered"
            // endpoint lands in a follow-up slice.
            selection.selectAllFiltered(products.map((p) => p.id));
          }}
          onClearSelection={() => selection.clear()}
          onActivate={() => setActivateModalOpen(true)}
          onDeactivate={() => setDeactivateModalOpen(true)}
          onPrice={() => setPriceModalOpen(true)}
          onCategory={() => setCategoryModalOpen(true)}
          onThreshold={() => setThresholdModalOpen(true)}
          onExportCsv={() => setExportModalOpen(true)}
          onDelete={() => setDeleteModalOpen(true)}
          isLoading={bulkActions.isAnyLoading}
        />

        {/* Bulk modals */}
        <BulkActivateModal
          open={activateModalOpen}
          onOpenChange={setActivateModalOpen}
          products={selectedProducts}
          onConfirm={handleBulkActivate}
          isLoading={bulkActions.activate.isPending}
        />
        <BulkDeactivateModal
          open={deactivateModalOpen}
          onOpenChange={setDeactivateModalOpen}
          products={selectedProducts}
          onConfirm={handleBulkDeactivate}
          isLoading={bulkActions.deactivate.isPending}
        />
        <BulkDeleteModal
          open={deleteModalOpen}
          onOpenChange={setDeleteModalOpen}
          products={selectedProducts}
          onConfirm={handleBulkDelete}
          isLoading={bulkActions.bulkDelete.isPending}
        />
        <BulkPriceModal
          open={priceModalOpen}
          onOpenChange={setPriceModalOpen}
          products={selectedProducts}
          onConfirm={handleBulkPrice}
          isLoading={bulkActions.price.isPending}
        />
        <BulkCategoryModal
          open={categoryModalOpen}
          onOpenChange={setCategoryModalOpen}
          products={selectedProducts}
          onConfirm={handleBulkCategory}
          isLoading={bulkActions.category.isPending}
        />
        <BulkThresholdModal
          open={thresholdModalOpen}
          onOpenChange={setThresholdModalOpen}
          products={selectedProducts}
          onConfirm={handleBulkThreshold}
          isLoading={bulkActions.threshold.isPending}
        />
        <BulkExportModal
          open={exportModalOpen}
          onOpenChange={setExportModalOpen}
          products={selectedProducts}
          onConfirm={handleExportCsv}
          isLoading={false}
        />

        {/* Command palette */}
        <ProductCommandPalette
          open={cmdPaletteOpen}
          onOpenChange={setCmdPaletteOpen}
          products={products}
          onSelectProduct={(p) => handleEdit(p)}
          onAdjustStock={handleOpenStockDrawer}
          onExportCsv={() => setExportModalOpen(true)}
          onNewProduct={handleNewProduct}
          onImportCsv={() => setImportOpen(true)}
          onViewAlerts={() => navigate("/admin/produits?stock=alerte")}
          onViewApprovals={() => navigate("/admin/produits/approbations")}
        />

        {/* Keyboard shortcuts cheatsheet */}
        <KeyboardShortcutsCheatsheet
          open={shortcutsOpen}
          onOpenChange={setShortcutsOpen}
        />
      </div>
    </AdminLayout>
  );
}

// ---------------------------------------------------------------------------
// Product detail view modal (read-only)
// ---------------------------------------------------------------------------

import { AdminModal } from "@/components/admin/shared/AdminModal";
import { ProductImage } from "@/components/admin/products/ProductImage";
import {
  BRAND_LABEL_MAP,
  CATEGORY_LABEL_MAP,
} from "@/lib/validation/product";

const FCFA_FMT = new Intl.NumberFormat("fr-SN", {
  style: "decimal",
  maximumFractionDigits: 0,
});

function ProductDetailModal({
  product,
  onClose,
}: {
  product: Product;
  onClose: () => void;
}) {
  const rows: readonly { label: string; value: string }[] = [
    { label: "Nom", value: product.name },
    { label: "Nom (Wolof)", value: product.name_wo || "—" },
    { label: "SKU", value: product.sku },
    { label: "Marque", value: BRAND_LABEL_MAP[product.brand] ?? product.brand },
    {
      label: "Catégorie",
      value: CATEGORY_LABEL_MAP[product.category] ?? product.category,
    },
    { label: "Prix TTC", value: `${FCFA_FMT.format(product.price_fcfa)} FCFA` },
    {
      label: "TVA",
      value: product.tva_applicable
        ? `${(product.tva_rate * 100).toFixed(0)}%`
        : "Exonéré",
    },
    { label: "Poids", value: `${product.weight_kg} kg` },
    { label: "Unité", value: product.unit },
    { label: "Qté min. commande", value: String(product.min_order_qty) },
    { label: "Stock", value: String(product.stock) },
    { label: "Statut", value: product.active ? "Actif" : "Inactif" },
    { label: "Vedette", value: product.is_featured ? "Oui" : "Non" },
    { label: "Produit CIEDA", value: product.is_cieda_brand ? "Oui" : "Non" },
  ];

  return (
    <AdminModal open onClose={onClose} title="Détails du produit" size="md">
      <div className="space-y-4">
        <ProductImage
          src={product.image_url}
          alt={`Produit ${product.name} — CIEDA`}
          size="lg"
          className="h-48 w-full overflow-hidden rounded-lg border border-stone-200"
        />

        <dl className="divide-y divide-stone-100">
          {rows.map((r) => (
            <div key={r.label} className="flex justify-between gap-4 py-2.5">
              <dt className="text-sm text-stone-500">{r.label}</dt>
              <dd className="text-sm font-medium text-stone-900">{r.value}</dd>
            </div>
          ))}
        </dl>

        {product.description && (
          <div>
            <p className="text-xs font-medium uppercase text-stone-500">
              Description
            </p>
            <p className="mt-1 text-sm text-stone-700">{product.description}</p>
          </div>
        )}
      </div>
    </AdminModal>
  );
}
